/**
 * Ponte OAuth Google para o app mobile (WebView React Native).
 *
 * Problema resolvido: o `code_verifier` do PKCE ficava só em useRef nos hooks.
 * Ao voltar do navegador do sistema (deep link), a WebView pode remontar e o ref
 * se perde — o code chega mas não troca por tokens nem salva no SCIM.
 *
 * Solução: persistir fluxo + verifier em sessionStorage e processar o retorno
 * em um listener global (SessionContext), salvando no mesmo campo SCIM do web.
 */

import { toast } from "react-toastify";
import { DeviceIDPatch } from "@/services/scim/profile/DeviceIDPatch";
import { setGoogleFitOAuthClient } from "@/services/google/health";

/** Fluxos OAuth mobile suportados (Agenda vs Google Health). */
export type GoogleMobileOAuthFlow = "calendar" | "healthcare";

const STORAGE_FLOW_KEY = "google_mobile_oauth_flow";
const STORAGE_VERIFIER_KEY = "google_mobile_oauth_verifier";

const GOOGLE_MOBILE_CLIENT_ID =
  import.meta.env.VITE_GOOGLE_CLIENT_ID_MOBILE ||
  import.meta.env.VITE_GOOGLE_CLIENT_ID;

const GOOGLE_MOBILE_REDIRECT_URI =
  import.meta.env.VITE_GOOGLE_MOBILE_REDIRECT_URI || "fluxmed://oauth2redirect";

/** Campos SCIM onde cada fluxo persiste o refresh token (igual ao web). */
const SCIM_REFRESH_TOKEN_PATH: Record<GoogleMobileOAuthFlow, string> = {
  calendar:
    "urn:ietf:params:scim:schemas:extension:enterprise:2.0:User:google_refresh_token",
  healthcare:
    "urn:ietf:params:scim:schemas:extension:enterprise:2.0:User:google_fit_refresh_token",
};

const ACCESS_TOKEN_STORAGE_KEY: Record<GoogleMobileOAuthFlow, string> = {
  calendar: "googleAccessToken",
  healthcare: "googleHealthcareAccessToken",
};

/** Eventos customizados para os hooks atualizarem loading/UI. */
export const GOOGLE_MOBILE_OAUTH_SUCCESS_EVENT = "google-mobile-oauth-success";
export const GOOGLE_MOBILE_OAUTH_ERROR_EVENT = "google-mobile-oauth-error";

export interface GoogleMobileOAuthBridgeDeps {
  getUserId: () => string | undefined;
  fetchUser: () => Promise<unknown>;
  setAccessTokenGoogle?: (token: string) => void;
}

let bridgeDeps: GoogleMobileOAuthBridgeDeps | null = null;
let listenerInstalled = false;

function isValidFlow(value: string | null): value is GoogleMobileOAuthFlow {
  return value === "calendar" || value === "healthcare";
}

/** Marca fluxo pendente antes de abrir o OAuth no navegador do sistema. */
export function markGoogleMobileOAuthPending(
  flow: GoogleMobileOAuthFlow,
  verifier: string
): void {
  sessionStorage.setItem(STORAGE_FLOW_KEY, flow);
  sessionStorage.setItem(STORAGE_VERIFIER_KEY, verifier);
}

/** Lê e remove fluxo pendente (uso único ao processar o code). */
export function consumeGoogleMobileOAuthPending(): {
  flow: GoogleMobileOAuthFlow;
  verifier: string;
} | null {
  const flowRaw = sessionStorage.getItem(STORAGE_FLOW_KEY);
  const verifier = sessionStorage.getItem(STORAGE_VERIFIER_KEY) || "";

  if (!isValidFlow(flowRaw) || !verifier) {
    return null;
  }

  sessionStorage.removeItem(STORAGE_FLOW_KEY);
  sessionStorage.removeItem(STORAGE_VERIFIER_KEY);
  return { flow: flowRaw, verifier };
}

/** Indica se há OAuth mobile aguardando retorno (para hooks reagirem a erros). */
export function hasGoogleMobileOAuthPending(): boolean {
  return Boolean(
    sessionStorage.getItem(STORAGE_FLOW_KEY) &&
      sessionStorage.getItem(STORAGE_VERIFIER_KEY)
  );
}

/** Cancela fluxo pendente sem trocar tokens. */
export function clearGoogleMobileOAuthPending(): void {
  sessionStorage.removeItem(STORAGE_FLOW_KEY);
  sessionStorage.removeItem(STORAGE_VERIFIER_KEY);
}

function dispatchOAuthEvent(name: string, detail?: Record<string, unknown>): void {
  window.dispatchEvent(new CustomEvent(name, { detail }));
}

function parseNativeMessageData(raw: unknown): Record<string, unknown> | null {
  let data: unknown = raw;
  if (typeof data === "string") {
    try {
      data = JSON.parse(data);
    } catch {
      return null;
    }
  }
  if (!data || typeof data !== "object") return null;
  return data as Record<string, unknown>;
}

/**
 * Troca authorization code por tokens (PKCE) e salva refresh token no SCIM.
 * Mesmo destino usado pelo fluxo web em DeviceIDPatch.
 */
export async function exchangeGoogleMobileCodeAndPersist(
  code: string,
  flow: GoogleMobileOAuthFlow,
  verifier: string,
  deps: GoogleMobileOAuthBridgeDeps
): Promise<void> {
  const userId = deps.getUserId();
  if (!userId) {
    throw new Error("Usuário não identificado para salvar token Google no SCIM");
  }

  const tokenResponse = await fetch("https://oauth2.googleapis.com/token", {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: new URLSearchParams({
      client_id: GOOGLE_MOBILE_CLIENT_ID,
      code,
      code_verifier: verifier,
      grant_type: "authorization_code",
      redirect_uri: GOOGLE_MOBILE_REDIRECT_URI,
    }),
  });

  if (!tokenResponse.ok) {
    const errorData = await tokenResponse.json().catch(() => ({}));
    console.error("Erro ao trocar código OAuth Google (mobile):", errorData);
    throw new Error("Falha ao obter tokens Google (mobile)");
  }

  const tokens = (await tokenResponse.json()) as {
    access_token?: string;
    refresh_token?: string;
  };

  if (!tokens.refresh_token) {
    throw new Error(
      "Refresh token não recebido. Remova o acesso do app no Google e tente novamente."
    );
  }

  await DeviceIDPatch({
    idUser: userId,
    operation: "replace",
    pathName: SCIM_REFRESH_TOKEN_PATH[flow],
    valueField: tokens.refresh_token,
  });

  if (tokens.access_token) {
    localStorage.setItem(ACCESS_TOKEN_STORAGE_KEY[flow], tokens.access_token);
    if (flow === "calendar" && deps.setAccessTokenGoogle) {
      deps.setAccessTokenGoogle(tokens.access_token);
    }
  }

  if (flow === "healthcare") {
    // Refresh token do client Android — Web precisa renovar sem secret
    setGoogleFitOAuthClient("mobile");
    localStorage.setItem("reopenFitnessModalAfterLink", "true");
  }

  await deps.fetchUser();
}

async function handleNativeOAuthCode(code: string): Promise<void> {
  if (!bridgeDeps) return;

  const pending = consumeGoogleMobileOAuthPending();
  if (!pending) return;

  try {
    await exchangeGoogleMobileCodeAndPersist(
      code,
      pending.flow,
      pending.verifier,
      bridgeDeps
    );

    if (pending.flow === "healthcare") {
      toast.success("Conta Google Health conectada com sucesso.");
    }

    dispatchOAuthEvent(GOOGLE_MOBILE_OAUTH_SUCCESS_EVENT, {
      flow: pending.flow,
    });
  } catch (error) {
    console.error("Erro ao persistir tokens Google (mobile):", error);
    if (pending.flow === "healthcare") {
      toast.error("Não foi possível salvar a conexão Google Health.");
    }
    dispatchOAuthEvent(GOOGLE_MOBILE_OAUTH_ERROR_EVENT, {
      flow: pending.flow,
      message: error instanceof Error ? error.message : "unknown",
    });
  }
}

function handleNativeOAuthError(message?: string): void {
  if (!hasGoogleMobileOAuthPending()) return;

  const flow = sessionStorage.getItem(STORAGE_FLOW_KEY);
  clearGoogleMobileOAuthPending();

  if (message && message !== "cancelled") {
    console.warn("Login Google (mobile) falhou:", message);
  }

  dispatchOAuthEvent(GOOGLE_MOBILE_OAUTH_ERROR_EVENT, {
    flow,
    message,
  });
}

function onWindowMessage(event: MessageEvent): void {
  const data = parseNativeMessageData(event.data);
  if (!data) return;

  if (data.type === "GOOGLE_OAUTH_CODE") {
    const payload = data.payload as { code?: string } | undefined;
    if (payload?.code) {
      void handleNativeOAuthCode(payload.code);
    }
    return;
  }

  if (data.type === "GOOGLE_OAUTH_ERROR") {
    const payload = data.payload as { message?: string } | undefined;
    handleNativeOAuthError(payload?.message);
  }
}

/**
 * Instala listener global (uma vez) para processar retorno OAuth do app nativo.
 * Deve ser chamado no SessionContext quando o usuário estiver autenticado.
 */
export function installGoogleMobileOAuthListener(
  deps: GoogleMobileOAuthBridgeDeps
): void {
  bridgeDeps = deps;

  if (listenerInstalled) return;
  listenerInstalled = true;
  window.addEventListener("message", onWindowMessage);
}

/** Abre OAuth no navegador do sistema via ponte React Native. */
export function requestGoogleMobileOAuthInNativeBrowser(payload: {
  url: string;
  redirectUri: string;
  flow: GoogleMobileOAuthFlow;
}): void {
  const webView = (window as any).ReactNativeWebView;
  if (!webView?.postMessage) {
    throw new Error("ReactNativeWebView indisponível");
  }

  webView.postMessage(
    JSON.stringify({
      type: "GOOGLE_OAUTH",
      payload: {
        url: payload.url,
        redirectUri: payload.redirectUri,
        flow: payload.flow,
      },
    })
  );
}

export { GOOGLE_MOBILE_CLIENT_ID, GOOGLE_MOBILE_REDIRECT_URI };
